import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Modal, Popconfirm, Row, Table, TableColumnsType, TableProps, Typography } from "antd";
import axios from "axios";
import { SearchBar } from "@bindeleddet/core/components";
import { message } from "@bindeleddet/core/utils";
import { Article } from "@bindeleddet/core/types";
import MobileTable from "components/MobileTable/MobileTable";
import { useViewport } from "@bindeleddet/core/hooks";
import { useEffect, useState } from "react";
import CreateOrEditArticle from "./CreateOrEditArticle";

const AdminArticles = () => {
  const { Title } = Typography;
  const { width } = useViewport();

  const [articles, setArticles] = useState<Article[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<Article | undefined>(undefined);

  useEffect(() => {
    axios
      .get<Article[]>("/website-contents/articles/")
      .then(response => {
        setArticles(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleSearch = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const getFilteredArticles = () =>
    articles.filter(article => {
      if (searchTerm === "") {
        return article;
      }
      if (article.title.toLowerCase().includes(searchTerm.toLowerCase())) {
        return article;
      }
      if (article.content.toLowerCase().includes(searchTerm.toLowerCase())) {
        return article;
      }
      return false;
    });

  const filteredArticles =
    articles &&
    getFilteredArticles().map(article => {
      return article;
    });

  type OnChange = NonNullable<TableProps<Article>["onChange"]>;
  type Sorts = {
    columnKey: string;
    order: "ascend" | "descend" | null;
  };

  const [sortedInfo, setSortedInfo] = useState<Sorts>({ columnKey: "created_at", order: "descend" });

  const handleChange: OnChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter as Sorts);
  };

  const handleCreateOrEdit = (article: Article) => {
    if (selectedArticle) {
      setArticles(articles.map(a => (a.id === article.id ? article : a)));
    } else {
      setArticles([article, ...articles]);
    }
    setIsModalOpen(false);
    setSelectedArticle(undefined);
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const columns: TableColumnsType<Article> = [
    {
      title: "Tittel",
      key: "title",
      dataIndex: "title",
      sorter: (a: Article, b: Article) => {
        return a.title.localeCompare(b.title);
      },
      sortOrder: sortedInfo.columnKey === "title" ? sortedInfo.order : null,
      ellipsis: true,
    },
    {
      title: "Innhold",
      dataIndex: "content",
      key: "content",
      ellipsis: true,
      render: (text: string) => {
        return <span>{stripHtml(text)}</span>;
      },
    },
    {
      title: "Publiseringsdato",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: Article, b: Article) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA.getTime() - dateB.getTime();
      },
      sortOrder: sortedInfo.columnKey === "created_at" ? sortedInfo.order : null,
      defaultSortOrder: "descend",
      render: (text, record) => {
        const date = new Date(record.created_at);
        return date.toLocaleDateString();
      },
      ellipsis: true,
      width: 200,
    },
    {
      key: "edit",
      render: (text, record) => (
        <EditOutlined
          onClick={() => {
            setSelectedArticle(record);
            setIsModalOpen(true);
          }}
          style={{ fontSize: 20, cursor: "pointer" }}
        />
      ),
      width: 50,
    },
    {
      key: "delete",
      render: (text, record) => (
        <Popconfirm
          title={`Slett "${record.title}"`}
          okText="Slett"
          cancelText="Avbryt"
          onConfirm={() => {
            axios
              .delete(`/website-contents/articles/${record.id}/`)
              .then(() => {
                message("success", "Artikkel slettet");
                setArticles(articles.filter(a => a.id !== record.id));
              })
              .catch(() => message("error", "Kunne ikke slette artikkel"));
          }}
        >
          <DeleteOutlined style={{ color: "red", fontSize: 20, cursor: "pointer" }} />
        </Popconfirm>
      ),
      width: 50,
    },
  ];

  return (
    <>
      <Card>
        <Row justify="space-between" align="middle">
          <Title level={2}>Artikler</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedArticle(undefined);
              setIsModalOpen(true);
            }}
          >
            Opprett ny artikkel
          </Button>
        </Row>
        <Row style={{ margin: "25px auto", width: "100%" }}>
          <SearchBar value={searchTerm} placeholder="Søk..." handleSearch={handleSearch} />
        </Row>
        {width > 768 ? (
          <Table
            columns={columns}
            rowKey="id"
            dataSource={filteredArticles}
            loading={loading}
            onChange={handleChange}
          />
        ) : (
          <MobileTable
            columns={[
              {
                title: "Tittel",
                dataIndex: "title",
                key: "title",
              },
              {
                title: "Publiseringsdato",
                dataIndex: "created_at",
                key: "created_at",
                render: value =>
                  new Date(value as string).toLocaleDateString("nb-NO", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }),
              },
            ]}
            data={filteredArticles}
            loading={loading}
            onClick={id => {
              const article = articles.find(a => a.id === id);
              if (article) {
                setSelectedArticle(article);
                setIsModalOpen(true);
              }
            }}
            emptyText="Ingen artikler funnet"
          />
        )}
      </Card>
      <Modal
        title={<Title level={3}>{selectedArticle ? "Oppdaterer artikkel" : "Oppretter artikkel"}</Title>}
        centered
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedArticle(undefined);
        }}
        width="70%"
        footer={null}
        bodyStyle={{ overflowY: "auto", maxHeight: "calc(100vh - 200px)", padding: "20px" }}
      >
        <CreateOrEditArticle
          newArticle={!selectedArticle}
          article={selectedArticle}
          handleCreateOrEdit={handleCreateOrEdit}
        />
      </Modal>
    </>
  );
};

export default AdminArticles;
